import React from 'react';

import {
  Box,
  Card,
  Stack,
  styled,
  Typography
} from '@mui/material';
import {
  baseTypes,
  imageUtil
} from 'modules';

import * as StatefulDownloadButton from './components/StatefulDownloadButton';



const RelativeImageWrapperBox = styled(Box)({
  position: 'relative'
});

const ThumbnailBox = styled(Card)({
  boxShadow: '-1px 3px 6px rgba(0, 0, 0, 0.1)',
  borderRadius: '5px'
});

const ThumbnailImage = styled('img')({
  position: 'relative',
  width:'100%'
});

const CenteredBox = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
});

const DescriptionBox = styled(Box)({
  width: '85%',
  maxWidth: 'fit-content',
  display: 'flex',
  flexWrap: 'wrap',
  textAlign: 'start'
});

const TitleTypography = styled(Typography)(({ theme }) => ({
  color: '#1E3949',
  fontSize: 20,
  fontWeight: 500,
  marginBottom: theme.spacing(0)
}));


interface IBaseProps {
  title: string;
  description?: React.ReactNode;
  onClickImage?: () => void;
  apiFileData?: baseTypes.IApiFileData;
  maxWidth?: number;
  height?: number;
  layoutVariant?: 'document' | 'slide' | 'proForma';
  showSnackbarHint?: boolean;
}

type IProps = IBaseProps & (
  { thumbnailFilename: string; }
  | { apiFileUrl: string; }
)

export const FC: React.FC<IProps> = props => {
  const styleVariants = __getStyleVariants(props);

  return (
    <Stack spacing={3} maxWidth={styleVariants.maxWidth}>
      <RelativeImageWrapperBox>
        <ThumbnailBox sx={styleVariants.thumbnailBoxStyle}>
          {__getShadowBox(props.layoutVariant)}
          <ThumbnailImage
            sx={{ zIndex: styleVariants.imageElevation }}
            src={styleVariants.imageSource}
            alt={props.title}
            onClick={props.onClickImage} />
        </ThumbnailBox>
      </RelativeImageWrapperBox>

      <Box>
        <CenteredBox>
          <Box width={styleVariants.titleWidth}>
            <TitleTypography>
              {props.title}
            </TitleTypography>
          </Box>

          {props.apiFileData && (
            <StatefulDownloadButton.FC
              apiFileData={props.apiFileData}
              color={styleVariants.downloadButtonColor}
              showSnackbarHint={props.showSnackbarHint}
            />
          )}
        </CenteredBox>

        <DescriptionBox>{props.description}</DescriptionBox>
      </Box>
    </Stack>
  );
};


function __getStyleVariants(props: IProps) {
  return {
    maxWidth: props.maxWidth ?? 400,
    thumbnailBoxStyle: __getThumbnailBoxStyle(props),
    imageSource: 'thumbnailFilename' in props
      ? imageUtil.getUrlFromFilename(props.thumbnailFilename)
      : props.apiFileUrl,
    titleWidth: props.layoutVariant === 'proForma' ? '70%' : '100%',
    downloadButtonColor: props.layoutVariant === 'proForma' ? '#528898' : '#87ACB9',
    imageElevation: props.layoutVariant === 'document' ? 2 : 0
  };
}


function __getThumbnailBoxStyle(props: Pick<IProps, 'height' | 'onClickImage' | 'layoutVariant'>) {
  return {
    height: props.height ?? 'auto',
    boxShadow: props.layoutVariant === 'proForma' ? '0px 3px 26px rgba(89, 131, 169, 0.14)' : '-1px 3px 6px rgba(0, 0, 0, 0.1)',
    ':hover': { cursor: props.onClickImage ? 'pointer' : 'default' }
  };
}



function __getShadowBox(variant: IProps['layoutVariant']) {
  if (!variant) {
    return null;
  }



  if (variant === 'proForma') {
    const BaseShadowBox = styled(Box)({
      position: 'absolute',
      width: '98%',
      height: '98%',
      background: '#87ACB9',
      borderRadius: 5,
      zIndex: -1
    });
    return (
      <>
        <BaseShadowBox style={{
          transform: 'rotate(3deg)',
          top: -3,
          right: -10
        }} />
        <BaseShadowBox style={{
          transform: 'rotate(-3deg)',
          top: -3,
          left: -10
        }} />
      </>
    );
  }



  const BaseShadowBox = styled(Box)({
    position: 'absolute',
    width: '100%',
    height: '100%',
    background: '#C3D1D8',
    borderRadius: 5
  });

  if (variant === 'slide') {
    return (
      <BaseShadowBox style={{
        transform: 'rotate(-4.2deg)',
        top: 0,
        right: 0,
        zIndex: -1
      }} />
    );
  } else if (variant === 'document') {
    return (
      <BaseShadowBox style={{
        top: -12,
        right: -12,
        zIndex: 1
      }} />
    );
  }



  return null;
}
